import type { OrganizationData } from '@typings/api/storefronts';
import { atom, type createStore } from 'jotai/vanilla';
import type { Bio } from '../../../types/vendor/properties';

export interface OrganizationState {
	travelAvailability: number | null;
	travelRange: string | null;
	startYear: number | null;
	totalMembers: number | null;
	teamSize: string | null;
	videographyServices: boolean;
	photographyServices: boolean;
	languages: string[] | null;
	teamMembers: Bio[];
}

const initialState: OrganizationState = {
	travelAvailability: null,
	travelRange: null,
	startYear: null,
	totalMembers: null,
	videographyServices: false,
	photographyServices: false,
	languages: null,
	teamMembers: [],
	teamSize: null,
};

export const travelAvailabilityAtom = atom(initialState.travelAvailability);
export const travelRangeAtom = atom(initialState.travelRange);
export const startYearAtom = atom(initialState.startYear);
export const totalMembersAtom = atom(initialState.totalMembers);
export const teamSizeAtom = atom(initialState.teamSize);
export const videographyServicesAtom = atom(initialState.videographyServices);
export const photographyServicesAtom = atom(initialState.photographyServices);
export const languagesAtom = atom(initialState.languages);
export const teamMembersAtom = atom(initialState.teamMembers);

export const setOrganizationData = (
	store: ReturnType<typeof createStore>,
	organizationData: OrganizationData['storefront'],
) => {
	if (organizationData.orgDetails) {
		store.set(
			travelAvailabilityAtom,
			organizationData.orgDetails.travelAvailability,
		);
		store.set(travelRangeAtom, organizationData.orgDetails.travelRange);
		store.set(startYearAtom, organizationData.orgDetails.startYear);
		store.set(totalMembersAtom, organizationData.orgDetails.totalMembers);
		store.set(teamSizeAtom, organizationData.orgDetails.teamSize);
		store.set(
			videographyServicesAtom,
			organizationData.orgDetails.videographyServices,
		);
		store.set(
			photographyServicesAtom,
			organizationData.orgDetails.photographyServices,
		);
		store.set(languagesAtom, organizationData.orgDetails.languages);
	}

	store.set(teamMembersAtom, organizationData.teamMembers);
};

export const getOrganizationDataToHydrate = (
	store: ReturnType<typeof createStore>,
) => {
	return {
		travelAvailability: store.get(travelAvailabilityAtom),
		travelRange: store.get(travelRangeAtom),
		startYear: store.get(startYearAtom),
		totalMembers: store.get(totalMembersAtom),
		teamSize: store.get(teamSizeAtom),
		videographyServices: store.get(videographyServicesAtom),
		photographyServices: store.get(photographyServicesAtom),
		languages: store.get(languagesAtom),
		teamMembers: store.get(teamMembersAtom),
	};
};

export const getOrganizationInitialValues = (
	values: ReturnType<typeof getOrganizationDataToHydrate>,
) => {
	const {
		travelAvailability,
		travelRange,
		startYear,
		totalMembers,
		videographyServices,
		photographyServices,
		languages,
		teamMembers,
		teamSize,
	} = values || {};

	return [
		[
			travelAvailabilityAtom,
			travelAvailability || initialState.travelAvailability,
		],
		[travelRangeAtom, travelRange || initialState.travelRange],
		[startYearAtom, startYear || initialState.startYear],
		[totalMembersAtom, totalMembers || initialState.totalMembers],
		[
			videographyServicesAtom,
			videographyServices || initialState.videographyServices,
		],
		[
			photographyServicesAtom,
			photographyServices || initialState.photographyServices,
		],
		[languagesAtom, languages || initialState.languages],
		[teamMembersAtom, teamMembers || initialState.teamMembers],
		[teamSizeAtom, teamSize || initialState.teamSize],
	];
};
